import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Converts a currency input string to a number of cents (integers).
// Example: "1,234.56" -> 123456
export function currencyFieldToNumber(value: string): number {
  if (!value) return 0;
  const onlyDigits = value.replace(/\D/g, "");
  return Number(onlyDigits || 0);
}

// Format a numeric amount using the provided currency code.
export function formatCurrency(amount: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currencyCode,
    }).format(amount ?? 0);
  } catch {
    // Fallback if currency code is invalid
    return `${currencyCode} ${amount?.toFixed?.(2) ?? amount}`;
  }
}

export function buildQueryParams(params: Record<string, string>) {
  return new URLSearchParams(Object.entries(params).filter(([_, v]) => v !== null && v !== undefined && v !== ""));
}

export function parseDate(dateString: string | undefined): Date | null {
  if (!dateString) return null;
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTimeBR(dateString: string) {
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString("pt-BR"),
    time: date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}

// Brazil dropped DST in 2019; America/Sao_Paulo is fixed at UTC-3.
const BR_OFFSET = "-03:00";

export function getStartOfDayISO(dateStr: string) {
  return new Date(`${dateStr}T00:00:00.000${BR_OFFSET}`).toISOString();
}

export function getEndOfDayISO(dateStr: string) {
  return new Date(`${dateStr}T23:59:59.999${BR_OFFSET}`).toISOString();
}

// Today's calendar date in São Paulo (fixed UTC-3, no DST since 2019),
// formatted "YYYY-MM-DD". Brazilian providers count "today" this way, whatever
// the timezone of the server or of the admin's browser.
export function todaySaoPauloISO(now: Date = new Date()): string {
  return new Date(now.getTime() - 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

// Yesterday's calendar date in São Paulo (fixed UTC-3, no DST since 2019),
// formatted "YYYY-MM-DD". `now` is injectable for deterministic tests.
export function yesterdaySaoPauloISO(now: Date = new Date()): string {
  const brt = new Date(now.getTime() - 3 * 60 * 60 * 1000); // UTC instant -> BRT wall clock
  brt.setUTCDate(brt.getUTCDate() - 1); // minus one calendar day
  return brt.toISOString().slice(0, 10);
}

// Days from the 1st of the previous month through `today` ("YYYY-MM-DD"), both
// included: how far back the admin month-to-date chart has to read.
export function daysFromPreviousMonthStart(today: string): number {
  const [y, m, d] = today.split("-").map(Number);
  const previousMonthLength = new Date(Date.UTC(y, m - 1, 0)).getUTCDate(); // day 0 = last day of last month
  return previousMonthLength + d;
}

export type SalesPeriod = "day" | "week" | "month";

// First São Paulo day ("YYYY-MM-DD") of today's day, week and month. Weeks open
// on Sunday, like the admin sales calendar.
export function salesPeriodStarts(today: string): Record<SalesPeriod, string> {
  const [y, m, d] = today.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(d - date.getUTCDay());
  return { day: today, week: date.toISOString().slice(0, 10), month: today.slice(0, 8) + "01" };
}

// "EDISON CRISTOVAO" -> "Edison Cristovao". Preserves words like O'Brien
// and McDonald only as Mcdonald — good enough for general-purpose names.
export function toTitleCase(input: string | null | undefined): string {
  if (!input) return "";
  return input
    .toLowerCase()
    .replace(/(^|\s|-|')\p{L}/gu, (m) => m.toUpperCase());
}

export function toYYYYMMDD(date: Date | string): string {
  if (typeof date === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    return date.split('T')[0];
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Mascara o root (3 digitos do meio) de um CNPJ.
 * Ex: "64089441000194" -> "64.***.441/0001-94"
 * Ex: "64.089.441/0001-94" -> "64.***.441/0001-94" (idempotente)
 *
 * Fallback: retorna input se nao tiver 14 digitos numericos.
 */
export function maskCnpj(cnpj: string | null | undefined): string {
  if (!cnpj) return "";
  const digits = cnpj.replace(/\D/g, "");
  if (digits.length !== 14) return cnpj;
  return `${digits.slice(0, 2)}.***.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

/**
 * Formata CNPJ completo (SEM mascarar) para exibição:
 * "64089441000194" -> "64.089.441/0001-94".
 * Usado onde o valor precisa ficar legível E copiável por inteiro (ex: chave PIX
 * de depósito manual). Fallback: retorna o input intacto quando não são 14
 * dígitos (chave PIX de email/telefone/aleatória).
 */
export function formatCnpj(value: string | null | undefined): string {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 14) return value;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

/**
 * Mascara documento brasileiro: CPF (11 digitos) ou CNPJ (14 digitos).
 * - CPF "12345678901" -> "123.***.789-01"
 * - CNPJ "64089441000194" -> "64.***.441/0001-94"
 *
 * Fallback: retorna input se nao tiver 11 nem 14 digitos.
 */
export function maskDocument(doc: string | null | undefined): string {
  if (!doc) return "";
  const digits = doc.replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.***.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  }
  if (digits.length === 14) {
    return maskCnpj(digits);
  }
  return doc;
}